import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  useTheme,
  alpha,
  styled,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import { ShipmentStatusDistribution } from '../../services/dashboardService';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
}));

const StatusDot = styled(Box)<{ color: string }>(({ color }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: 8,
}));

const CustomTooltip = ({ active, payload }: any) => {
  const theme = useTheme();
  
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2,
          boxShadow: theme.customShadows.z8,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
          {data.status.replace('_', ' ')}
        </Typography>
        <Typography variant="body2" color="primary.main">
          Count: {data.count}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data.percentage}% of total
        </Typography>
      </Box>
    );
  }
  return null;
};

interface StatusDistributionChartProps {
  data: ShipmentStatusDistribution[];
  title?: string;
  height?: number;
}

export default function StatusDistributionChart({ 
  data, 
  title = "Shipment Status Distribution",
  height = 300 
}: StatusDistributionChartProps) {
  const theme = useTheme();

  // Define colors for different statuses
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return theme.palette.warning.main;
      case 'in_transit': return theme.palette.info.main;
      case 'out_for_delivery': return theme.palette.secondary.main;
      case 'delivered': return theme.palette.success.main;
      case 'cancelled': return theme.palette.error.main;
      case 'returned': return alpha(theme.palette.error.main, 0.7);
      default: return theme.palette.grey[400];
    }
  };

  const COLORS = data.map(item => getStatusColor(item.status));

  const formatLabel = (status: string) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <StyledCard>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Distribution of shipment statuses
          </Typography>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <Box display="flex" alignItems="center" gap={3}>
          {/* Pie Chart */}
          <Box sx={{ width: '60%', height }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="count"
                >
                  {data.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]}
                      stroke={theme.palette.background.paper}
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* Legend */}
          <Box sx={{ width: '40%' }}>
            <List dense sx={{ py: 0 }}>
              {data.map((item, index) => (
                <ListItem key={item.status} sx={{ py: 0.5, px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                    <StatusDot color={COLORS[index % COLORS.length]} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatLabel(item.status)}
                      </Typography>
                    }
                    secondary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {item.count} shipments
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600,
                            color: COLORS[index % COLORS.length]
                          }}
                        >
                          {item.percentage}%
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
}
